import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { BrandingProvider } from "./context/BrandingContext";
import { PWAProvider } from "./context/PWAContext";
import { FeaturesProvider } from "./context/FeaturesContext";
import { TenantProvider } from "./context/TenantContext";
import OfflineBadge from "./components/pwa/OfflineBadge";
import InstallBanner from "./components/pwa/InstallBanner";

// Native-app detection — adds `is-native-app` class to <html> when the
// storefront is running inside the Capacitor iOS/Android wrapper. Used
// by index.css to apply native-app polish (no hover states, larger
// tap targets, blurred sticky headers, hidden desktop chrome, etc.).
// Addresses App Store Guideline 4.2 rejections that flag the app as a
// webview wrapping a website.
//
// Additionally tags the <html> with `is-ios` whenever the user agent
// looks like iPhone / iPad / iPod so the CSS in index.css can force
// >= 16px font-size on all inputs to prevent the WebKit focus auto-
// zoom (belt-and-braces alongside the @supports rule).
if (typeof window !== "undefined") {
  // The Capacitor runtime injects `window.Capacitor` before our
  // React bundle boots. Checking `isNativePlatform()` is the
  // canonical signal — treats both iOS and Android identically.
  const isNative = !!window.Capacitor?.isNativePlatform?.();
  if (isNative) {
    document.documentElement.classList.add("is-native-app");
    // An app screen doesn't pinch-zoom. `touch-action: manipulation` only
    // stops double-tap zoom, so in WKWebView a pinch left the whole UI
    // zoomed and panning around. The website keeps zoom (accessibility).
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute("content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover");
    }
  }

  // iOS detection — covers Safari, WKWebView (Capacitor), and iPadOS 13+
  // which spoofs Mac Safari in navigator.userAgent (detected via touch).
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document) ||
    window.Capacitor?.getPlatform?.() === "ios";
  if (isIOS) {
    document.documentElement.classList.add("is-ios");
  }

  // Loyalty referral — pick up `?ref=CODE` from the URL a friend shared
  // and stash in localStorage. Consumed once by AuthContext on the
  // NEXT OTP verify (i.e. only applied to a brand-new signup). Ignored
  // if the visitor is already signed in for this tenant.
  try {
    const p = new URLSearchParams(window.location.search);
    const ref = (p.get("ref") || "").trim().toUpperCase();
    if (ref && /^[A-Z0-9]{4,12}$/.test(ref)) {
      localStorage.setItem("kb_referral_code", ref);
    }
  } catch { /* ignore */ }
}

import StoreHeader from "./components/layout/StoreHeader";
import StoreFooter from "./components/layout/StoreFooter";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import DashboardLayout from "./components/layout/DashboardLayout";

import Home from "./pages/store/Home";
import Listing from "./pages/store/Listing";
import ProductDetail from "./pages/store/ProductDetail";
import Cart from "./pages/store/Cart";
import Wishlist from "./pages/store/Wishlist";
import Checkout from "./pages/store/Checkout";
import OrderSuccess from "./pages/store/OrderSuccess";
import OrderDetail from "./pages/store/OrderDetail";
import Account from "./pages/store/Account";
import CampaignPage from "./pages/store/CampaignPage";
import SharedCartView from "./pages/store/SharedCartView";
import StyleFeed from "./pages/store/StyleFeed";
import LegalPage from "./pages/store/LegalPage";

import SellerOnboard from "./pages/seller/SellerOnboard";
import SellerDashboard from "./pages/seller/SellerDashboard";
import SellerProducts from "./pages/seller/SellerProducts";
import SellerProductForm from "./pages/seller/SellerProductForm";
import SellerOrders from "./pages/seller/SellerOrders";
import SellerInventory from "./pages/seller/SellerInventory";
import SellerCoupons from "./pages/seller/SellerCoupons";
import SellerAnalytics from "./pages/seller/SellerAnalytics";
import SellerSettings from "./pages/seller/SellerSettings";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSellers from "./pages/admin/AdminSellers";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminBulkUpload from "./pages/admin/AdminBulkUpload";
import AdminBulkUploadLog from "./pages/admin/AdminBulkUploadLog";
import AdminSmartCatalog from "./pages/admin/AdminSmartCatalog";
import AdminAttributes from "./pages/admin/AdminAttributes";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import AccountRewards from "./pages/account/AccountRewards";
import AdminReports from "./pages/admin/AdminReports";
import AdminSections from "./pages/admin/AdminSections";
import AdminReturns from "./pages/admin/AdminReturns";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminCampaigns from "./pages/admin/AdminCampaigns";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminBranding from "./pages/admin/AdminBranding";
import AdminHero from "./pages/admin/AdminHero";
import AdminHomeSections from "./pages/admin/AdminHomeSections";
import AdminLegal from "./pages/admin/AdminLegal";
import AdminPushBroadcast from "./pages/admin/AdminPushBroadcast";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminWallet from "./pages/admin/AdminWallet";
import AdminUpgrade from "./pages/admin/AdminUpgrade";
import AdminDomain from "./pages/admin/AdminDomain";
import SuperAdminHome from "./pages/superadmin/SuperAdminHome";
import SuperAdminFeatures from "./pages/superadmin/SuperAdminFeatures";
import SuperAdminPlans from "./pages/superadmin/SuperAdminPlans";
import SuperAdminTenants from "./pages/superadmin/SuperAdminTenants";
import SuperAdminAnalytics from "./pages/superadmin/SuperAdminAnalytics";
import SuperAdminSettings from "./pages/superadmin/SuperAdminSettings";
import SuperAdminWallets from "./pages/superadmin/SuperAdminWallets";
import SuperAdminWalletDetail from "./pages/superadmin/SuperAdminWalletDetail";
import SuperAdminPricing from "./pages/superadmin/SuperAdminPricing";
import SuperAdminFirebase from "./pages/superadmin/SuperAdminFirebase";
import SuperAdminRenewals from "./pages/superadmin/SuperAdminRenewals";
import FeatureGate from "./components/common/FeatureGate";
import ScrollToTop from "./components/common/ScrollToTop";
import StartStore from "./pages/StartStore";
import ScratchCardAnnouncer from "./components/store/ScratchCardAnnouncer";

function StoreLayout({ children }) {
  return (
    <>
      <StoreHeader />
      <main className="min-h-[70vh] pb-20 lg:pb-0">{children}</main>
      <StoreFooter />
      <MobileBottomNav />
      <ScratchCardAnnouncer />
    </>
  );
}

function RoleRoute({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<StoreLayout><Home /></StoreLayout>} />
      <Route path="/start-store" element={<StartStore />} />
      <Route path="/shop" element={<StoreLayout><Listing /></StoreLayout>} />
      <Route path="/shop/:category" element={<StoreLayout><Listing /></StoreLayout>} />
      <Route path="/product/:slug" element={<StoreLayout><ProductDetail /></StoreLayout>} />
      <Route path="/cart" element={<StoreLayout><Cart /></StoreLayout>} />
      <Route path="/cart/shared/:token" element={<StoreLayout><SharedCartView /></StoreLayout>} />
      <Route path="/wishlist" element={<StoreLayout><Wishlist /></StoreLayout>} />
      <Route path="/checkout" element={<StoreLayout><Checkout /></StoreLayout>} />
      <Route path="/order/:id/success" element={<StoreLayout><OrderSuccess /></StoreLayout>} />
      <Route path="/order/:id" element={<StoreLayout><OrderDetail /></StoreLayout>} />
      <Route path="/account" element={<StoreLayout><Account /></StoreLayout>} />
      <Route path="/account/rewards" element={<StoreLayout><AccountRewards /></StoreLayout>} />
      <Route path="/campaign/:slug" element={<StoreLayout><CampaignPage /></StoreLayout>} />
      <Route path="/reels" element={<StoreLayout><StyleFeed /></StoreLayout>} />
      <Route path="/legal/:kind" element={<StoreLayout><LegalPage /></StoreLayout>} />
      <Route path="/about" element={<StoreLayout><LegalPage kind="about" /></StoreLayout>} />
      <Route path="/contact" element={<StoreLayout><LegalPage kind="contact" /></StoreLayout>} />

      <Route path="/seller/onboard" element={<StoreLayout><SellerOnboard /></StoreLayout>} />
      <Route
        path="/seller/*"
        element={
          <RoleRoute roles={["seller", "admin"]}>
            <DashboardLayout variant="seller">
              <Routes>
                <Route index element={<SellerDashboard />} />
                <Route path="products" element={<SellerProducts />} />
                <Route path="products/new" element={<SellerProductForm />} />
                <Route path="products/:id/edit" element={<SellerProductForm />} />
                <Route path="inventory" element={<SellerInventory />} />
                <Route path="orders" element={<SellerOrders />} />
                <Route path="coupons" element={<SellerCoupons />} />
                <Route path="analytics" element={<SellerAnalytics />} />
                <Route path="settings" element={<SellerSettings />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route
        path="/admin/*"
        element={
          <RoleRoute roles={["admin"]}>
            <DashboardLayout variant="admin">
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="products/bulk-upload" element={<FeatureGate featureKey="bulk_upload" title="Bulk Upload is locked"><AdminBulkUpload /></FeatureGate>} />
                <Route path="products/bulk-upload/:id" element={<FeatureGate featureKey="bulk_upload" title="Bulk Upload is locked"><AdminBulkUploadLog /></FeatureGate>} />
                <Route path="products/smart-catalog" element={<FeatureGate featureKey="ai_smart_catalog" title="AI Smart Catalog is locked"><AdminSmartCatalog /></FeatureGate>} />
                <Route path="sellers" element={<AdminSellers />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="attributes" element={<AdminAttributes />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<SellerProductForm />} />
                <Route path="products/:id/edit" element={<SellerProductForm />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="returns" element={<AdminReturns />} />
                <Route path="refunds" element={<AdminRefunds />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="hero" element={<AdminHero />} />
                <Route path="home-sections" element={<AdminHomeSections />} />
                <Route path="sections" element={<AdminSections />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="social" element={<FeatureGate featureKey="social_reels" title="Reels is locked"><AdminSocial /></FeatureGate>} />
                <Route path="branding" element={<AdminBranding />} />
                {/* Gated by the tenant's Bulk / Scheduled notification switches
                    (super-admin, per tenant, enforced by the API) inside the page. */}
                <Route path="push" element={<AdminPushBroadcast />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="loyalty" element={<AdminLoyalty />} />
                <Route path="reports" element={<FeatureGate featureKey="advanced_analytics" title="Reports is locked"><AdminReports /></FeatureGate>} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="wallet" element={<AdminWallet />} />
                <Route path="upgrade" element={<AdminUpgrade />} />
                <Route path="domain" element={<AdminDomain />} />
                <Route path="legal" element={<AdminLegal />} />
                <Route path="settings" element={<AdminSettings />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route
        path="/superadmin/*"
        element={
          <RoleRoute roles={["super_admin"]}>
            <DashboardLayout variant="super">
              <Routes>
                <Route index element={<SuperAdminHome />} />
                <Route path="features" element={<SuperAdminFeatures />} />
                <Route path="plans" element={<SuperAdminPlans />} />
                <Route path="tenants" element={<SuperAdminTenants />} />
                <Route path="renewals" element={<SuperAdminRenewals />} />
                <Route path="analytics" element={<SuperAdminAnalytics />} />
                <Route path="wallets" element={<SuperAdminWallets />} />
                <Route path="wallets/:id" element={<SuperAdminWalletDetail />} />
                <Route path="pricing" element={<SuperAdminPricing />} />
                <Route path="firebase" element={<SuperAdminFirebase />} />
                <Route path="settings" element={<SuperAdminSettings />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // When the URL is /t/{slug}/..., use that prefix as the BrowserRouter
  // basename so all existing relative routes work unchanged for the tenant.
  let basename = "";
  if (typeof window !== "undefined") {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts[0] === "t" && parts[1]) basename = `/t/${parts[1]}`;
  }
  return (
    <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <BrandingProvider>
            <PWAProvider>
              <FeaturesProvider>
                <BrowserRouter basename={basename}>
                  <ScrollToTop />
                  <AppRoutes />
                  <OfflineBadge />
                  <InstallBanner />
                  <Toaster
                    position="top-right"
                    richColors
                    // Below the status bar / notch / Dynamic Island, not under it
                    // (sonner's own gaps — 24px, 16px on phones — on top of the inset).
                    offset={{ top: "calc(env(safe-area-inset-top, 0px) + 24px)" }}
                    mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
                  />
                </BrowserRouter>
              </FeaturesProvider>
            </PWAProvider>
          </BrandingProvider>
        </CartProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
